import type { SuggestedAddress } from './types';

type PlaceAddressComponent = {
  longText?: string | null;
  shortText?: string | null;
  types?: readonly string[] | null;
};

type PlaceLike = {
  addressComponents?: readonly PlaceAddressComponent[] | null;
  formattedAddress?: string | null;
};

const getComponentText = (components: readonly PlaceAddressComponent[], type: string, useShortText = false): string => {
  const match = components.find((component) => component.types?.includes(type));

  if (!match) {
    return '';
  }

  const text = useShortText ? match.shortText : match.longText;
  return text || match.longText || match.shortText || '';
};

const getFirstComponentText = (
  components: readonly PlaceAddressComponent[],
  types: readonly string[],
  useShortText = false
): string => {
  return types.reduce((found, type) => found || getComponentText(components, type, useShortText), '');
};

/**
 * Normalizes a resolved Google Place's `addressComponents` (and
 * `formattedAddress` fallback) into a plain, form-shaped address. Every
 * consumer of `<AddressSuggestion />` gets the same field parsing instead of
 * re-implementing Google's component-type matching.
 */
export const mapPlaceToAddress = (place: PlaceLike): SuggestedAddress => {
  const components = place.addressComponents || [];
  const streetNumber = getComponentText(components, 'street_number');
  const route = getComponentText(components, 'route');
  const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim();
  const formattedAddress = place.formattedAddress || '';
  const formattedFirstLine = formattedAddress.split(',')[0]?.trim() || '';

  return {
    address1: streetAddress || formattedFirstLine,
    address2: getComponentText(components, 'subpremise'),
    city: getFirstComponentText(components, ['locality', 'postal_town', 'sublocality_level_1']),
    state: getComponentText(components, 'administrative_area_level_1', true),
    postalCode: getComponentText(components, 'postal_code', true),
    country: getComponentText(components, 'country', true),
    formattedAddress,
  };
};
